import { useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { ThemedEditIcon } from "assets/CXFIcons/Portal/Profile";
import { ThemedPowerOff, ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import { ThemedCheckIcon, ThemedInfoIcon } from "assets/CXFIcons/Static";
import SoftAvatar from "components/SoftAvatar";
import SoftBox from "components/SoftBox";
import { ComponentListItemIcon, ComponentListItemButton } from "../StyledComponents";
import { THEME_COLORS } from "constants/theme";
import { TRIGGER_ICON_BY_TYPE, TRIGGER_TYPES } from "constants/flowDesigner";
import {
   openTriggerPropertiesPanel,
   setIsTitlebarTriggerListOpen,
   setSelectedTrigger,
   setTriggerForDelete,
   addFlowTrigger,
   openTriggerDeleteDialog,
   startDragTrigger,
   stopDragTrigger,
   setIsTitlebarExecuteTriggerListOpen
} from "redux/actions/flowDesigner/triggersActionCreator";
import { addContextTrigger, toggleContextTrigger, hideContextPanel } from "redux/actions/flowDesigner/editorContextActionCreator";
import useCheckTriggersDuplicate from "hooks/useCheckTriggersDuplicate";
import { toggleTrigger } from "redux/actions/flowDesigner/triggersActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";
import { hideStartNodePanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { FLEX_LAYOUT_COMPONENT } from "constants/flowDesigner";

const styles = {
   componentMenuIcon: (theme) => ({
      color: THEME_COLORS[theme.palette.mode.toUpperCase()]?.PRIMARY_FONT
   })
};

const TriggerMenuItem = ({ trigger = {}, customStyles = {}, allowDescription, isTitlebarAction, executeTriggersList, handleExecuteFlowByTrigger = Function(), setNodeStartTriggerListOpen = Function(), allowEditWorkflow }) => {
   const { t } = useTranslation("flowDesigner");
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const { flowTriggers } = useSelector((store) => store?.designer?.triggers);
   const { autoSaveFlow } = useContext(TabContextApi);
   const { checkIfTriggerDuplicate } = useCheckTriggersDuplicate();
   const dispatch = useDispatch();
   const activeTrigger = flowTriggers.find((item) => item.type === trigger.type);
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const { addLayoutTab } = useFlexLayout();

   const handleAddLayoutTab = (tab) => () => {
      dispatch(setSelectedTrigger(activeTrigger));
      dispatch(openTriggerPropertiesPanel());
      addLayoutTab(tab, `startNodeProps`, `Start Node`, { trigger });
   };
   const { type, label } = trigger;

   const openTriggerDeletePopup = (e) => {
      e.stopPropagation();
      dispatch(setTriggerForDelete(trigger));
      dispatch(openTriggerDeleteDialog());
      setNodeStartTriggerListOpen(false);
      dispatch(setIsTitlebarExecuteTriggerListOpen(false));
   };

   const addTrigger = async () => {
      if (currentSavedFlow?.shared_status === "active" || currentSavedFlow?.shared_status === "inactive" || !allowEditWorkflow) return;

      if (checkIfTriggerDuplicate(trigger.type)) {
         return; // just block it, don't open props panel
      }

      try {
         if (currentSavedFlow?.id) {
            dispatch(addFlowTrigger(trigger));
            dispatch(addContextTrigger(trigger.label, currentSavedFlow?.name));
            autoSaveFlow();
         }
      } catch (e) {
         console.error(e);
      }
   };

   const onDragStart = (event) => {
      event.dataTransfer.setData("movedTrigger", JSON.stringify(trigger));
      dispatch(startDragTrigger());
   };

   const onDragEnd = (event) => {
      event.preventDefault();
      dispatch(stopDragTrigger());
   };

   const toggleTriggerState = () => {
      dispatch(toggleTrigger(trigger));
      dispatch(toggleContextTrigger(trigger.label, currentSavedFlow?.name));
      autoSaveFlow();
   };

   const showTriggerPropsPanel = (e) => {
      e.stopPropagation();
      dispatch(hideContextPanel());
      dispatch(setSelectedTrigger(activeTrigger));

      dispatch(hideStartNodePanel());

      setNodeStartTriggerListOpen(false);
      dispatch(setIsTitlebarExecuteTriggerListOpen(false));
      dispatch(openTriggerPropertiesPanel());
      addLayoutTab(FLEX_LAYOUT_COMPONENT.triggerProps, `startNodeProps`, `Start Node`, {
         trigger: activeTrigger || trigger
      });

      setTimeout(() => {
         dispatch(setIsTitlebarTriggerListOpen(false));
      }, 300);
   };

   const isTriggerActive = () => {
      return !!activeTrigger && !isTitlebarAction;
   };

   const TriggerIcon = ({ type }) => {
      const IconComponent = TRIGGER_ICON_BY_TYPE[type];
      return <IconComponent />;
   };

   if (allowDescription) {
      return (
         <ListItem sx={{ padding: "4px 10px 4px 5px" }}>
            {isTitlebarAction && trigger.type !== TRIGGER_TYPES.DEFAULT /*&& !executeTriggersList*/ ? (
               <Box
                  className="trigger-list-item"
                  sx={{
                     display: "flex",
                     alignItems: "center",
                     gap: 1,
                     justifyContent: "space-between",
                     width: "100%"
                  }}
               >
                  <Box sx={{ display: "flex", alignItems: "center", opacity: trigger.disabled ? 0.3 : 1 }}>
                     <ComponentListItemIcon sx={{ ...customStyles }}>
                        <TriggerIcon type={type} sx={(theme) => styles.componentMenuIcon(theme)} />
                     </ComponentListItemIcon>
                     <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                           sx: { fontSize: "13px", textOverflow: "ellipsis", overflow: "hidden" }
                        }}
                     />
                  </Box>
                  {!isFlowActive && allowEditWorkflow ? (
                     <SoftBox display="flex" gap={1} sx={{ marginLeft: "32px" }} className="trigger-list-actions">
                        <Tooltip title={t("buttons.edit")} placement="bottom-start">
                           <IconButton aria-label="edit" size="small" sx={(theme) => styles.componentMenuIcon(theme)} onClick={handleAddLayoutTab(FLEX_LAYOUT_COMPONENT.triggerProps)}>
                              <ThemedEditIcon hoverColor="#7443FF" />
                           </IconButton>
                        </Tooltip>
                        <Tooltip title={t(`buttons.${trigger.disabled ? "enable_trigger" : "disable_trigger"}`)} placement="bottom-start">
                           <IconButton aria-label="disable" size="small" style={{ width: "32px" }} onClick={toggleTriggerState} disabled={!allowEditWorkflow}>
                              <ThemedPowerOff hoverColor="#7443FF" />
                           </IconButton>
                        </Tooltip>
                        <Tooltip title={t("buttons.delete")} placement="bottom-start">
                           <IconButton aria-label="delete" size="small" sx={(theme) => styles.componentMenuIcon(theme)} onClick={openTriggerDeletePopup} disabled={!allowEditWorkflow}>
                              <ThemedTrashIcon hoverColor="#7443FF" />
                           </IconButton>
                        </Tooltip>
                     </SoftBox>
                  ) : (
                     <SoftBox display="flex" gap={1} sx={{ marginLeft: "32px" }} className="trigger-list-actions">
                        <Tooltip title={t("View")} placement="bottom-start">
                           <IconButton
                              aria-label="view"
                              size="small"
                              sx={(theme) => ({
                                 ...styles.componentMenuIcon(theme),
                                 "&:hover svg": { color: "#7443ff" }
                              })}
                              onClick={showTriggerPropsPanel}
                           >
                              <ThemedInfoIcon width="19px" height="19px" hoverColor="#7443FF" />
                           </IconButton>
                        </Tooltip>
                     </SoftBox>
                  )}
               </Box>
            ) : (
               <ComponentListItemButton
                  role={undefined}
                  onClick={() => (executeTriggersList ? handleExecuteFlowByTrigger(type) : addTrigger())}
                  dense
                  sx={{ maxWidth: 300 }}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  draggable={allowEditWorkflow}
                  disabled={!allowEditWorkflow || !currentSavedFlow}
               >
                  <ComponentListItemIcon sx={{ ...customStyles }}>
                     <TriggerIcon type={type} sx={(theme) => styles.componentMenuIcon(theme)} />
                  </ComponentListItemIcon>
                  <ListItemText
                     primary={label}
                     primaryTypographyProps={{
                        sx: { fontSize: "13px", textOverflow: "ellipsis", overflow: "hidden" }
                     }}
                  />
                  {isTriggerActive() && (
                     <ComponentListItemIcon sx={{ ...customStyles }}>
                        <ThemedCheckIcon />
                     </ComponentListItemIcon>
                  )}
               </ComponentListItemButton>
            )}
         </ListItem>
      );
   }

   return (
      <Tooltip key="arrow-rect" title={label}>
         <SoftAvatar size="xs">
            <TriggerIcon type={type} sx={(theme) => styles.componentMenuIcon(theme)} />
         </SoftAvatar>
      </Tooltip>
   );
};

export default TriggerMenuItem;
